<template>
  <DashboardPageLayout
    title="Sales"
    :is-loading="isLoading"
    @change="onDateRangeChange"
  >
    <template v-if="showSupervisorFilter" #filters>
      <b-form-group
        label="Supervisor"
        label-for="dashboard-sales-supervisor"
      >
        <ImageTextSelect
          id="dashboard-sales-supervisor"
          :model-value="filterSupervisorId"
          :options="supervisorFilterOptions"
          :fallback-image="supervisorPictureFallback"
          @update:model-value="onSupervisorFilterChange"
        />
      </b-form-group>
    </template>

    <template #summary>
      <div class="text-muted small">
        {{ formattedDateRange }}
      </div>
      <div v-if="salesOrdersByDay" class="font-weight-bold">
        Total orders: {{ $formatNumber(salesOrdersByDay.total, 0) }}
      </div>
      <div
        v-if="orderTotalsVsRefundsByDay"
        class="font-weight-bold mt-25 d-flex flex-wrap align-items-center"
        style="gap: 1rem;"
      >
        <span
          class="text-nowrap"
          :style="{ color: totalsColor }"
        >
          Order totals: {{ $formatCurrency(orderTotalsVsRefundsByDay.total_orders_amount, null) }}
        </span>
        <span
          class="text-nowrap"
          :style="{ color: refundsColor }"
        >
          Refunds: {{ $formatCurrency(orderTotalsVsRefundsByDay.total_refunds_amount, null) }}
        </span>
      </div>
    </template>

    <template #default="{ isLoading: chartLoading }">
      <DashboardChartCard
        title="Order quantity by day"
        type="bar"
        card-class="mb-2"
        :is-loading="chartLoading"
        :has-data="quantityChartHasData"
        empty-text="No orders in the selected date range."
        :options="quantityChartOptions"
        :series="quantityChartSeries"
      />

      <DashboardChartCard
        title="Order totals vs refunds"
        type="line"
        :is-loading="chartLoading"
        :has-data="totalsChartHasData"
        :options="totalsChartOptions"
        :series="totalsChartSeries"
      />
    </template>
  </DashboardPageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { BFormGroup } from 'bootstrap-vue'
import { $themeColors } from '@themeConfig'
import DashboardPageLayout from '@/views/Components/Elements/DashboardPageLayout.vue'
import DashboardChartCard from '@/views/Components/Elements/DashboardChartCard.vue'
import ImageTextSelect from '@/views/Components/Elements/ImageTextSelect.vue'
import dashboardPageMixin from '@/mixins/dashboardPageMixin'
import {
  buildDashboardChartOptions,
  buildDashboardChartViewFromStatistics,
} from '@/libs/dashboardChart'

const TOTALS_COLOR = '#0d6efd'
const REFUNDS_COLOR = '#ea5455'

export default {
  components: {
    BFormGroup,
    DashboardPageLayout,
    DashboardChartCard,
    ImageTextSelect,
  },
  mixins: [dashboardPageMixin],
  data() {
    return {
      dashboardLoadErrorMessage: 'Can not load sales statistics',
      totalsColor: TOTALS_COLOR,
      refundsColor: REFUNDS_COLOR,
    }
  },
  computed: {
    ...mapGetters({
      salesOrdersByDay: 'dashboard/salesOrdersByDay',
      orderTotalsVsRefundsByDay: 'dashboard/orderTotalsVsRefundsByDay',
    }),
    ordersLabel() {
      return this.companyPurpose !== 'rent' ? 'Orders' : 'Bookings'
    },
    quantityChartHasData() {
      return (this.salesOrdersByDay?.days || []).length > 0
    },
    quantityChartView() {
      return buildDashboardChartViewFromStatistics({
        statistics: this.salesOrdersByDay,
        selectedDateRange: this.selectedDateRange,
        series: [{ name: this.ordersLabel, key: 'count' }],
      })
    },
    quantityChartSeries() {
      return this.quantityChartView?.series || []
    },
    quantityChartOptions() {
      return buildDashboardChartOptions({
        chartView: this.quantityChartView,
        chartType: 'bar',
        colors: [$themeColors.primary],
        yAxisTitle: `${this.ordersLabel} quantity`,
        yAxisFormatter: (value) => Math.round(value).toLocaleString(this.$getNumberLocale()),
        tooltipYFormatter: (value) => `${Math.round(value).toLocaleString(this.$getNumberLocale())} ${this.ordersLabel.toLowerCase()}`,
      })
    },
    totalsChartHasData() {
      return (this.orderTotalsVsRefundsByDay?.days || []).length > 0
    },
    totalsChartView() {
      return buildDashboardChartViewFromStatistics({
        statistics: this.orderTotalsVsRefundsByDay,
        selectedDateRange: this.selectedDateRange,
        series: [
          { name: 'Order totals', key: 'orders_total' },
          { name: 'Refunds', key: 'refunds_total' },
        ],
      })
    },
    totalsChartSeries() {
      return this.totalsChartView?.series || []
    },
    totalsChartOptions() {
      return buildDashboardChartOptions({
        chartView: this.totalsChartView,
        chartType: 'line',
        colors: [this.totalsColor, this.refundsColor],
        yAxisTitle: 'Amount',
        yAxisFormatter: (value) => this.formatCurrency(value),
        tooltipYFormatter: (value) => this.formatCurrency(value),
        sharedTooltip: true,
      })
    },
  },
  methods: {
    ...mapActions({
      loadSalesOrdersByDay: 'dashboard/loadSalesOrdersByDay',
      loadOrderTotalsVsRefundsByDay: 'dashboard/loadOrderTotalsVsRefundsByDay',
    }),
    formatCurrency(value) {
      return this.$formatCurrency(value, null)
    },
    fetchDashboardData(payload) {
      return Promise.all([
        this.loadSalesOrdersByDay(payload),
        this.loadOrderTotalsVsRefundsByDay(payload),
      ])
    },
  },
}
</script>
